import React, { useEffect } from "react";
import { Container, Alert, ConnectedAlert, ProgressBar } from "../../../components";
import { Text, Heading } from "@telia/teddy";
import Cookies from "js-cookie";
import { connect } from "react-redux";
import { selectors as AUTH_SELECTORS } from "../..";
import { RootState } from "../../../../../models";
import {
    getErrorMessage,
    getFlashMessage,
    isOrderExpired as isOrderExpiredSelector,
    isOrderNotFound as isOrderNotFoundSelector,
} from "../../../app/state/selectors";
import BankIdSection from "../../../checkout/components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import TEXT from "../../text-consts";
import "./BankIdAuthentication.less";

interface StateProps {
    customer?: string;
    errorMessage?: string;
    isBankIdPerformed: boolean;
    isBankIdVerified: boolean;
    isErrorDoingAuthentication: boolean;
    isFetchingData: boolean;
    isOrderExpired: boolean;
    isOrderNotFound: boolean;
    shouldShowBankId: boolean;
}

type BankIdAuthenticationProps = StateProps;

const AUTH_COOKIE_NAME = "orderRef.authenticateUser";

const getAuthenticationCookieName = () => {
    const cookieNames = Object.keys(Cookies.get());

    return cookieNames.find(
        (cookieName) =>
            (cookieName === AUTH_COOKIE_NAME || cookieName.startsWith(`${AUTH_COOKIE_NAME}.`)) &&
            !cookieName.endsWith(".completed"),
    );
};

const getAuthenticationCookieOptions = () => {
    const hostname = window.location.hostname;
    const isTeliaDomain = hostname === "telia.no" || hostname.endsWith(".telia.no");

    return isTeliaDomain ? { path: "/", domain: ".telia.no" } : { path: "/" };
};

const BankIdAuthentication: React.FC<BankIdAuthenticationProps> = ({
    // Redux (data)
    isBankIdVerified,
    isBankIdPerformed,
    isFetchingData,
    isOrderExpired,
    isOrderNotFound,
    customer,
    shouldShowBankId,
    errorMessage,
}) => {
    const showBankId = !isOrderNotFound && !isOrderExpired && !isBankIdVerified && !isBankIdPerformed;
    const isCustomerAuthenticated = !shouldShowBankId && customer;

    useEffect(() => {
        if (isBankIdVerified && isCustomerAuthenticated) {
            const cookieName = getAuthenticationCookieName();

            if (!cookieName) {
                return;
            }

            const cookieValue = Cookies.get(cookieName);
            const cookieOptions = getAuthenticationCookieOptions();

            // Clear the active session cookie and replace with a completed marker
            Cookies.remove(cookieName, cookieOptions);
            Cookies.remove(cookieName, { path: "/" });

            if (!cookieValue) {
                return;
            }

            Cookies.set(`${cookieName}.completed`, cookieValue, {
                ...cookieOptions,
                expires: 30 / (24 * 60), // 30 min
            });
        }
    }, [isBankIdVerified, isCustomerAuthenticated]);
    return (
        <div className="bankid-auth-page">
            <div className="bankid-auth-page__heading-container">
                <Heading mb="100" as="h1" variant="title-600">
                    {TEXT.AUTH_PAGE.HEADING}
                </Heading>
            </div>
            <ProgressBar isFetchingData={isFetchingData} standByColor="light-grey" />
            <ConnectedAlert />
            <Container size="small">
                {isOrderExpired && (
                    <Text mb="100" variant="preamble-200">
                        {TEXT.AUTH_PAGE.ORDER_EXPIRED}
                    </Text>
                )}
                {isOrderNotFound && (
                    <Text mb="100" variant="preamble-200">
                        {TEXT.AUTH_PAGE.ORDER_NOT_FOUND}
                    </Text>
                )}
                {errorMessage && <Alert kind="warning">{errorMessage}</Alert>}
                {showBankId && (
                    <Container className="install-esim__bankid-container">
                        <BankIdSection referer="auth-user" skipDisplayHeading={true} />
                    </Container>
                )}
                {isBankIdVerified && isCustomerAuthenticated && (
                    <Text mb="100" variant="preamble-200" data-di-mask>
                        {TEXT.AUTH_PAGE.RESULT_TEXT(customer)}
                    </Text>
                )}
            </Container>
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    isBankIdVerified: AUTH_SELECTORS.isBankIdVerified(state),
    isBankIdPerformed: AUTH_SELECTORS.isBankIdPerformed(state),
    isErrorDoingAuthentication: !!getFlashMessage(state),
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    isOrderExpired: isOrderExpiredSelector(state),
    isOrderNotFound: isOrderNotFoundSelector(state),
    customer: AUTH_SELECTORS.getCustomer(state),
    shouldShowBankId: AUTH_SELECTORS.shouldShowBankId(state),
    errorMessage: getErrorMessage(state),
});

export default connect(mapStateToProps)(BankIdAuthentication);
